export type KeyValue = string | number | symbol

export { partitionBy } from './reducer'

/**
 * Returns a new array sorted by given sort function.
 *
 * @param list
 * @param by
 */
export function sortBy<T>(list: Array<T>, by: (a: T, b: T) => number): Array<T> {
  return Array.from(list).sort(by)
}

/**
 * Returns the first element of an array. This function does not check whether
 * the array holds any elements. It is supposed to be a composable, readable
 * version of `array[0]`.
 *
 * NOTE: It is the caller's responsibility to not pass an empty array. Return
 * type is not `T | undefined` explicitly, so that it can be used as shorthand.
 *
 * @param array
 */
export function first<T>(array: Array<T>): T {
  if (array.length === 0) throw new Error('Array is empty')

  return array[0]
}

/**
 * Returns the last element of an array. This function does not check the length
 * of the array. It is supposed to be a composable, readable version of
 * `array[array.length - 1]`.
 *
 * NOTE: It is the caller's responsibility to not pass an empty array. Return
 * type is not `T | undefined` explicitly, so that it can be used as shorthand.
 *
 * @param array
 */
export function last<T>(array: Array<T>): T {
  if (array.length === 0) throw new Error('Array is empty')

  return array[array.length - 1]
}

/**
 * Returns an array with `size` elements with values ranging from `0` to `size - 1`.
 *
 * @example
 * rangeUntil(4)
 * // => [0, 1, 2, 3]
 *
 * @param size
 */
export function range(size: number): Array<number> {
  return Array.from(Array(size).keys())
}

/**
 * Returns a new list with only unique values of given list
 *
 * @param list
 */
export function unique<T>(list: Array<T>): Array<T> {
  return [...new Set(list)]
}

/**
 * Returns a new list with only unique values using a key closure
 *
 * @param list
 */
export function uniqueBy<K, T>(list: Array<T>, by: (item: T) => K): Array<T> {
  const map: Map<K, T> = new Map()
  for (const item of list) {
    const key = by(item)
    map.set(key, item)
  }
  return [...map.values()]
}

/**
 *
 * @param list
 * @param by
 */
export function groupBy<K extends KeyValue, T>(list: Array<T>, by: (item: T) => K): Map<K, Array<T>> {
  const groups = new Map<K, Array<T>>()

  for (const item of list) {
    const key = by(item)
    const group = groups.get(key)
    if (group) {
      group.push(item)
    } else {
      groups.set(key, [item])
    }
  }

  return groups
}

/**
 *
 * return flattened distinct array of Map values
 *
 * e.g. flattenMapValues(Map(1 => ['dandy', 'rui', 'katie'], 2 => ['desi', 'mike', 'dandy']))
 *
 * returns ['dandy', 'rui', 'katie', 'desi', 'mike']
 */
export function flattenUniqueMapValues<K, V extends KeyValue>(map: Map<K, Array<V>>): Array<V> {
  return unique([...map.values()].flat())
}

/**
 * Returns a list with only truthy values. It filters our: NaN, undefined, null, 0, false
 * @param list
 */
export function filterTruthy<T>(list: Array<T | null | undefined>): Array<T> {
  return list.filter(Boolean) as Array<T>
}

/**
 * Pick the value of an object's property.
 *
 * @param item
 * @param property
 */
export function pick<T, U extends keyof T>(item: T, property: U): T[U] {
  return item[property]
}

/**
 * Pick a list of property values from a list of objects. If there are
 * items without given property, the list will also contain `undefined`.
 *
 * @param list
 * @param property
 */
export function pickFromList<T, U extends keyof T>(list: ReadonlyArray<T>, property: U): Array<T[U]> {
  return list.map((item) => pick(item, property))
}

/**
 * Pick a list of unique property values from a list of objects. If there are
 * items without given property, the list will also contain `undefined`.
 *
 * @param list
 * @param property
 */
export function pickUnique<T, U extends keyof T>(list: Array<T>, property: U): Array<T[U]> {
  return unique(pickFromList(list, property))
}

/**
 * Create a Map from a list. Keys are calculated with given function `by`,
 * values stay as they are.
 *
 * @example
 * toMapWithKey([1, 2, 3, 4], (n) => n * 3)
 * // => Map(
 * //      3 => 1,
 * //      6 => 2,
 * //      9 => 3,
 * //      12 => 4,
 * //    )
 *
 * @param list
 * @param by
 */
export function toMapWithKey<K extends KeyValue, T>(list: Array<T>, by: (item: T) => K): Map<K, T> {
  const map: Map<K, T> = new Map()
  for (const item of list) {
    const key = by(item)
    map.set(key, item)
  }
  return map
}

/**
 * Create a list from all values of a map
 *
 * @example
 * mapToList(Map(3 => 1, 6 => 2, 9 => 3, 12 => 4))
 * // => [1, 2, 3, 4]
 *
 * @param map
 */
export function mapToList<K extends KeyValue, T>(map: Map<K, T>): Array<T> {
  return Array.from(map.values())
}

/**
 * Split an array into same size chunks. Will return a smaller chunk at the
 * end if list length is not an exact multiple of size.
 *
 * @example
 * const list = [1, 2, 3, 4, 5, 6, 7, 8]
 * chunk(list, 2)
 * //=> [[1, 2], [3, 4], [5, 6], [7, 8]]
 *
 * @example
 * const list = [1, 2, 3, 4, 5, 6, 7, 8]
 * chunk(list, 3)
 * //=> [[1, 2, 3], [4, 5, 6], [7, 8]]
 *
 * @param list
 * @param size
 */
export function chunk<T>(list: Array<T>, size = 10): Array<Array<T>> {
  if (size === 0 || isNaN(size)) {
    return [list]
  }

  return list.reduce((chunks: Array<Array<T>>, item: T, index: number): Array<Array<T>> => {
    const current = Math.floor(index / size)

    if (!chunks[current]) {
      chunks[current] = [item]
    } else {
      chunks[current].push(item)
    }

    return chunks
  }, [])
}

/**
 * Removes nullish values (null and undefined) from an array and ensures the return type
 *
 * @example
 * removeNullish([0, false, undefined, '', null, 'pizza'])
 * // => [0, false, '', 'pizza']
 */
export function removeNullish<T>(array: Array<T | undefined | null>): Array<T> {
  return array.filter((value): value is T => value !== undefined && value !== null)
}

/**
 * Returns the intersection of two arrays based on a key function.
 *
 * @example
 * intersectBy([{id: 1}, {id: 2}, {id: 3}], [{id: 2}, {id: 3}, {id: 4}], (item) => item.id)
 * // => [{id: 2}, {id: 3}]
 */
export function intersectBy<T>(list1: Array<T>, list2: Array<T>, by: (item: T) => string | number): Array<T> {
  const map: Map<string | number, T> = new Map()
  for (const item of list1) {
    const key = by(item)
    map.set(key, item)
  }
  return list2.filter((item) => map.has(by(item)))
}

/**
 * Returns the difference between two arrays based on a key function.
 * The difference is calculated as list1 - list2.
 *
 * @example
 * differenceBy([{id: 1}, {id: 2}, {id: 3}], [{id: 2}, {id: 3}, {id: 4}], (item) => item.id)
 * // => [{id: 1}]
 */
export function differenceBy<T>(list1: Array<T>, list2: Array<T>, by: (item: T) => string | number): Array<T> {
  const map: Map<string | number, T> = new Map()
  for (const item of list2) {
    const key = by(item)
    map.set(key, item)
  }
  return list1.filter((item) => !map.has(by(item)))
}

export function sum(list: Array<number>): number {
  return list.reduce((sum, value) => sum + value, 0)
}

/**
 * Check if two arrays contain the same elements
 */
export function matchesItems<T extends string | number | boolean>(list1: Array<T>, list2: Array<T>): boolean {
  return list1.length === list2.length && list1.every((item) => list2.includes(item))
}

export function hasDuplicates<T>(list: Array<T>): boolean {
  return new Set(list).size !== list.length
}

export function head<T>(list: ArrayLike<T> | undefined | null): T | null {
  if (!list || list.length === 0) return null
  return list[0]
}
